import { IconCircleCheck, IconCircleX, IconLoader2 } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { SlotPlanJobDto } from "../availabilityApi"
import type { SlotPlanJobStatus } from "../lib/editorModel"

const STATUS_LABEL: Record<SlotPlanJobStatus, string> = {
  queued: "Queued",
  running: "Applying to Cal",
  succeeded: "Applied",
  failed: "Failed",
}

function when(instant: string | null | undefined): string | null {
  if (!instant) return null
  // The job's timestamps are UTC DateTimes, serialized without a zone.
  const value = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(instant) ? instant : `${instant}Z`)
  return Number.isNaN(value.getTime())
    ? null
    : value.toLocaleString("en-US", { timeZone: "America/Los_Angeles", dateStyle: "medium", timeStyle: "short" })
}

/** The save the page is waiting on, or the one that just finished: status, timings and each schedule's result. */
export function JobStatusPanel({
  job,
  status,
  onDismiss,
}: {
  job: SlotPlanJobDto | undefined
  status: SlotPlanJobStatus | null
  onDismiss: () => void
}) {
  const finished = status === "succeeded" || status === "failed"
  const requested = when(job?.requestedAt)
  const ended = when(job?.finishedAt)

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3" role="status" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        {status === "succeeded" ? (
          <IconCircleCheck className="size-4 text-green-600" />
        ) : status === "failed" ? (
          <IconCircleX className="size-4 text-destructive" />
        ) : (
          <IconLoader2 className="size-4 animate-spin" />
        )}
        <span className="text-sm font-medium">Last save</span>
        <Badge variant={status === "failed" ? "destructive" : status === "succeeded" ? "default" : "secondary"}>
          {status ? STATUS_LABEL[status] : "Checking…"}
        </Badge>
        {requested && <span className="text-xs text-muted-foreground">requested {requested}</span>}
        {ended && <span className="text-xs text-muted-foreground">· finished {ended}</span>}
        {finished && (
          <Button variant="ghost" size="sm" className="ml-auto" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
      {!finished && (
        <p className="text-xs text-muted-foreground">
          The changes are saved and being applied to the calendar. You can leave this page: you will be told
          when it finishes, and this page picks it up again when you come back.
        </p>
      )}
      {job && job.results.length > 0 && (
        <ul className="flex flex-col gap-1 text-xs">
          {job.results.map((result, index) => (
            <li key={`${result.target}-${index}`} className="flex items-start gap-1.5">
              {result.succeeded ? (
                <IconCircleCheck className="mt-px size-3.5 shrink-0 text-green-600" />
              ) : (
                <IconCircleX className="mt-px size-3.5 shrink-0 text-destructive" />
              )}
              <span>
                <span className="font-medium">{result.target}</span>
                {result.detail && <span className="text-muted-foreground"> — {result.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
